import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [link, setLink] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/password/forgot", { email });
      toast.success("Check your email for a reset link.");
      if (data.reset_link_dev) setLink(data.reset_link_dev);
    } catch (err) {
      toast.error(formatError(err, "Failed"));
    } finally { setSubmitting(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-slate-100 p-4">
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded p-6 max-w-md w-full">
        <div className="label-overline text-blue-700">Forgot password</div>
        <h1 className="text-2xl font-black tracking-tight mt-1 text-slate-900">Reset your password</h1>
        <p className="text-sm text-slate-600 mt-2">Enter your account email.</p>
        <div className="mt-4">
          <Label className="label-overline">Email</Label>
          <Input data-testid="forgot-email" type="email" required className="rounded h-11 mt-1 font-mono"
                 value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <Button type="submit" data-testid="forgot-submit" disabled={submitting}
                className="w-full h-11 mt-4 rounded bg-blue-700 hover:bg-blue-800">
          {submitting ? "Sending…" : "Send reset link"}
        </Button>
        {link && (
          <div data-testid="forgot-dev-link" className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs font-mono break-all">
            Dev link: <Link to={link} className="text-blue-700 underline">{link}</Link>
          </div>
        )}
        <div className="text-center mt-4">
          <Link to="/login" className="text-sm text-slate-600 hover:underline">Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}

export function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/password/reset", { token, new_password: pw });
      toast.success("Password reset. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatError(err, "Reset failed"));
    } finally { setSubmitting(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-slate-100 p-4">
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded p-6 max-w-md w-full">
        <div className="label-overline text-blue-700">Reset password</div>
        <h1 className="text-2xl font-black tracking-tight mt-1 text-slate-900">Set a new password</h1>
        <div className="mt-4">
          <Label className="label-overline">New password (min 8)</Label>
          <Input data-testid="reset-password" type="password" required minLength={8}
                 className="rounded h-11 mt-1 font-mono"
                 value={pw} onChange={(e) => setPw(e.target.value)} />
        </div>
        <Button type="submit" data-testid="reset-submit" disabled={submitting}
                className="w-full h-11 mt-4 rounded bg-blue-700 hover:bg-blue-800">
          {submitting ? "Saving…" : "Reset password"}
        </Button>
      </form>
    </div>
  );
}
